import { useEffect } from "react";
import styled from "@emotion/styled";

import { paper, fonts, shade, stickyA } from "../theme";
import { useWebSocket } from "../useWebSocket";
import * as haptics from "../haptics";
import type { GameOverView, PlayerInfo } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsPlate, MsReceipt, MsBtn, MsStamp,
    MaskHeadline, Confetti,
    Stack, Row,
} from "../ms";

/* Red ink on a gold wash — the one stamp that inverts the usual fill, because it
 * sits on the dark champion plate where a near-transparent red would vanish. */
const ChampionStamp = styled(MsStamp)`
    background: ${stickyA(0.95)};
    color: ${paper.red};
    border-color: ${paper.red};
`;

const ChampionRank = styled.div`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 84px;
    line-height: 0.85;
    color: ${paper.red};
    letter-spacing: -0.04em;
`;

const ChampionNameWrap = styled.div`
    flex: 1;
`;

const ChampionName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 32px;
    color: ${paper.paper};
`;

const StandingsEyebrow = styled(MsEyebrow)`
    margin-bottom: 4px;
`;

const MedalName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 20px;
    color: ${paper.ink};
`;

const MedalScore = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 22px;
    color: ${paper.ink};
    font-variant-numeric: tabular-nums;
`;

const ChampionDisplay = styled(MsDisplay)`
    font-size: 42px;
    line-height: 0.98;
`;

const ChampionPlate = styled(MsPlate)`
    margin-top: 6px;
`;

const ChampionRow = styled(Row)`
    margin-top: 12px;
`;

const MedalRow = styled.div<{ rank: number }>`
    display: grid;
    grid-template-columns: 36px 1fr auto;
    gap: 14px;
    align-items: center;
    padding: 14px 4px;
    border-bottom: 1px dashed ${shade(0.18)};

    &:last-child { border-bottom: 0; }
`;

const Rank = styled.div<{ rank: number }>`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 28px;
    line-height: 0.85;
    color: ${p => p.rank === 1 ? paper.red : paper.ink};
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
`;

interface Props {
    view: GameOverView;
    players: PlayerInfo[];
    self: PlayerInfo;
    resetGame: () => void;
}

export default function GameOver({ view, players, self, resetGame }: Props) {
    const { sendMessage } = useWebSocket();
    const { finalScores } = view;

    const getName = (id: string) => players.find(p => p.id === id)?.name || "Unknown";

    const ranked = Object.entries(finalScores || {})
        .sort(([, a], [, b]) => (b as number) - (a as number))
        .map(([playerId, score], index) => ({
            playerId,
            name: getName(playerId),
            score: score as number,
            rank: index + 1,
            you: playerId === self?.id,
        }));

    const champion = ranked[0];
    const rest = ranked.slice(1, 8);

    /* The champion plate and its confetti land together; this lands with them. */
    useEffect(() => {
        haptics.celebrate();
    }, []);

    const playAgain = () => sendMessage({ type: "start_game" });

    return (
        <MsScreen>
            <Confetti count={60} />
            <MsBody>
                <Stack gap={16}>
                    <ChampionDisplay>
                        <MaskHeadline>
                            {champion ? `${champion.name} wins.` : "Game over."}
                        </MaskHeadline>
                    </ChampionDisplay>

                    {champion && (
                        <ChampionPlate r1="-0.6deg">
                            <Row justify="space-between" align="center">
                                <MsEyebrow tone="sticky">Champion</MsEyebrow>
                                <ChampionStamp>
                                    {champion.score} pts
                                </ChampionStamp>
                            </Row>
                            <ChampionRow gap={16} align="flex-end">
                                <ChampionRank>01</ChampionRank>
                                <ChampionNameWrap>
                                    <ChampionName>
                                        {champion.name}{champion.you && " (you)"}
                                    </ChampionName>
                                </ChampionNameWrap>
                            </ChampionRow>
                        </ChampionPlate>
                    )}

                    {rest.length > 0 && (
                        <MsReceipt>
                            <StandingsEyebrow>Final standings</StandingsEyebrow>
                            {rest.map((p) => (
                                <MedalRow key={p.playerId} rank={p.rank}>
                                    <Rank rank={p.rank}>{String(p.rank).padStart(2, "0")}</Rank>
                                    <MedalName>
                                        {p.name}{p.you && " (you)"}
                                    </MedalName>
                                    <MedalScore>
                                        {p.score}
                                    </MedalScore>
                                </MedalRow>
                            ))}
                        </MsReceipt>
                    )}
                </Stack>
            </MsBody>
            <MsFooter>
                {self?.isHost && <MsBtn variant="red" onClick={playAgain}>Play again</MsBtn>}
                <MsBtn variant="ghost" onClick={resetGame}>Leave game</MsBtn>
            </MsFooter>
        </MsScreen>
    );
}
